import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@evocha/api-client";
import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Phone,
  ShieldCheck,
  TrendingUp,
  Zap,
} from "lucide-react";
import { dealerApi } from "../lib/api";
import { useDealerSession, type DealerProfile } from "../lib/session";

function errText(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Try again.";
}

export function Login() {
  const navigate = useNavigate();
  const { signIn, authenticated } = useDealerSession();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (authenticated) {
    navigate("/", { replace: true });
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{9,15}$/.test(phone.replace(/\s/g, ""))) {
      setError("Enter your phone number, e.g. 0754 000 000.");
      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      setError("Your PIN is 4 to 6 digits.");
      return;
    }
    setBusy(true);
    try {
      const res = await dealerApi.post<{ access_token: string; refresh_token: string; dealer: DealerProfile }>(
        "/superdealer/v1/auth/login",
        { phone: phone.replace(/\s/g, ""), pin },
      );
      signIn({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
        token_type: "Bearer",
        expires_in: 0,
        refresh_expires_in: 0,
        dealer: res.dealer,
      });
      navigate("/", { replace: true });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dealer-auth">
      <aside className="dealer-auth-art">
        <span className="dealer-auth-orb dealer-auth-orb-a" aria-hidden="true" />
        <span className="dealer-auth-orb dealer-auth-orb-b" aria-hidden="true" />

        <header className="dealer-auth-art-top">
          <div className="brand">
            <span className="brand-name">evocha</span>
            <span className="brand-line">Super Dealer</span>
          </div>
          <span className="dealer-auth-pill">TZS float network</span>
        </header>

        <div className="dealer-auth-art-copy">
          <h1>
            Push float to your agents
            <span>the moment they need it.</span>
          </h1>
          <p>
            One console for your wallet, your agent network and every transfer in
            between &mdash; balances refresh as transactions settle.
          </p>
          <ul className="dealer-auth-points">
            <li>
              <Zap size={16} />
              <span>
                <strong>Instant transfers.</strong> Float lands in an agent wallet in
                seconds, with a reference you can trace.
              </span>
            </li>
            <li>
              <ShieldCheck size={16} />
              <span>
                <strong>Every kodi accounted for.</strong> Statements and receipts stay
                in one place for reconciliation.
              </span>
            </li>
            <li>
              <TrendingUp size={16} />
              <span>
                <strong>Commissions in view.</strong> Track what your network earns, not
                what you have to guess.
              </span>
            </li>
          </ul>
        </div>

        <footer className="dealer-auth-art-foot">
          <span>evocha by SKT Tanzania</span>
          <span>Dealer console</span>
        </footer>
      </aside>

      <div className="dealer-auth-side">
        <div className="dealer-auth-mobile-brand">
          <span className="brand-name">evocha</span>
          <span className="brand-line">Super Dealer</span>
        </div>

        <form className="dealer-auth-card" onSubmit={submit} noValidate>
          <div className="dealer-auth-head">
            <h2>Dealer sign in</h2>
            <p>
              Use the phone number and PIN registered with your dealership.
            </p>
          </div>

          <div className="dealer-auth-fields">
            <label className="admin-field">
              <span>Phone number</span>
              <div className="dealer-input-wrap">
                <Phone size={16} className="dealer-input-icon" />
                <input
                  className="ev-input dealer-input-iconed dealer-auth-input"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="0754 000 000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={busy}
                />
              </div>
            </label>

            <label className="admin-field">
              <span className="dealer-auth-label-row">
                <span>PIN</span>
                <span className="dealer-auth-hint">4&ndash;6 digits</span>
              </span>
              <div className="dealer-input-wrap">
                <Lock size={16} className="dealer-input-icon" />
                <input
                  className="ev-input dealer-input-iconed dealer-auth-input dealer-auth-pin"
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  autoComplete="current-password"
                  maxLength={6}
                  placeholder="••••"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="ev-icon-btn dealer-pin-toggle"
                  aria-label={showPin ? "Hide PIN" : "Show PIN"}
                  onClick={() => setShowPin((p) => !p)}
                >
                  {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
          </div>

          {error && (
            <div className="dealer-alert dealer-alert-error" role="alert">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button className="ev-btn ev-btn-primary ev-btn-lg dealer-auth-submit" type="submit" disabled={busy}>
            {busy ? <Loader2 size={18} className="spin" /> : null}
            {busy ? "Signing in…" : "Sign in"}
            {!busy && <ArrowRight size={17} />}
          </button>

          <p className="dealer-auth-privacy">
            <ShieldCheck size={14} />
            Your PIN protects every transfer. Never share it with your agents.
          </p>
        </form>

        <p className="dealer-auth-foot">
          Forgot your PIN? Contact your Evocha account manager.
        </p>
      </div>
    </div>
  );
}
